import type { Component } from 'solid-js';
import { useMutation, useQueryClient } from '@tanstack/solid-query';
import { Undo2 } from 'lucide-solid';
import { ApiError } from '~/shared/api/http-client';
import { queryKeys } from '~/shared/api/query-keys';
import { tournamentApi } from '~/shared/api/tournament-api';
import { useCan } from '~/shared/access/useCan';
import { haptic } from '~/shared/lib/haptics';
import { Button } from '~/shared/ui/Button';
import { DialogShell } from '~/shared/ui/DialogShell';
import { useToast } from '~/shared/ui/Toast';

export const ResetBracketDialog: Component<{
  tournamentId: string;
  tournamentName: string;
  onClose: () => void;
}> = (props) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const canManage = useCan('/tournaments', 'manage');

  const resetMut = useMutation(() => ({
    mutationFn: () => tournamentApi.resetBracket(props.tournamentId),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournament.detail(props.tournamentId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournament.all() });
      toast.success(
        `Сетка расформирована: удалено раундов — ${r.discardedRounds}, матчей — ${r.discardedMatches}. Запись закрыта.`,
      );
      haptic('success');
      props.onClose();
    },
    onError: (err: unknown) => {
      toast.error(err instanceof ApiError ? err.message : 'Не удалось расформировать сетку');
      haptic('error');
    },
  }));

  const close = () => {
    if (!resetMut.isPending) props.onClose();
  };

  return (
    <DialogShell onClose={close} maxWidth="480px">
      <div class="flex flex-col gap-4 p-5">
        <div class="flex items-center gap-2.5">
          <Undo2 class="h-4.5 w-4.5 text-[var(--color-text-tertiary)]" aria-hidden="true" />
          <h2 class="text-[15px] font-semibold">Расформировать сетку</h2>
        </div>

        <p class="text-[12.5px] text-[var(--color-text-secondary)] leading-snug">
          Сетка турнира «{props.tournamentName}» будет удалена, турнир вернётся в «запись закрыта». Дальше можно
          открыть запись, добавить игроков, поменять окно и корты и сформировать заново.
        </p>

        <div class="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="md" onClick={close} disabled={resetMut.isPending}>
            Отмена
          </Button>
          <Button
            type="button"
            variant="danger"
            size="md"
            loading={resetMut.isPending}
            disabled={resetMut.isPending || !canManage()}
            onClick={() => resetMut.mutate()}
          >
            Расформировать
          </Button>
        </div>
      </div>
    </DialogShell>
  );
};
